import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './UserDashboard.css';
import ConfirmationModal from '../DashboardForm/ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../constants';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';
import { RiResetLeftFill } from "react-icons/ri";

interface Antrag {
  id: number;
  status: string;
  antragsdatum: string;
  vorname: string;
  nachname: string;
  land: string;
  ausbildungsland: string;
  neueNachricht: string;
  hatRechnung: boolean;
}

const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [antraege, setAntraege] = useState<Antrag[]>([]);
  const [filteredAntraege, setFilteredAntraege] = useState<Antrag[]>([]);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [antragToDelete, setAntragToDelete] = useState<number | null>(null);

  const [filterId, setFilterId] = useState('');
  const [filterVorname, setFilterVorname] = useState('');
  const [filterNachname, setFilterNachname] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTimestamp, setFilterTimestamp] = useState('');

  const [sortColumn, setSortColumn] = useState<keyof Antrag | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  // store filters in session storage
  const isInitialized = useRef(false); // needed in development modes
  const [filtersLoaded, setFiltersLoaded] = useState(false);


  useEffect(() => {
    if (isInitialized.current) {
      return;
    }
    const storedFilterId = sessionStorage.getItem('filterId') || '';
    const storedFilterVorname = sessionStorage.getItem('filterVorname') || '';
    const storedFilterNachname = sessionStorage.getItem('filterNachname') || '';
    const storedFilterStatus = sessionStorage.getItem('filterStatus') || '';
    const storedFilterTimestamp = sessionStorage.getItem('filterTimestamp') || '';

    setFilterId(storedFilterId);
    setFilterVorname(storedFilterVorname);
    setFilterNachname(storedFilterNachname);
    setFilterStatus(storedFilterStatus);
    setFilterTimestamp(storedFilterTimestamp);

    isInitialized.current = true;
    setFiltersLoaded(true);
  }, []);

  useEffect(() => {
    if (!filtersLoaded) {
      return;
    }
    sessionStorage.setItem('filterId', filterId);
    sessionStorage.setItem('filterVorname', filterVorname);
    sessionStorage.setItem('filterNachname', filterNachname);
    sessionStorage.setItem('filterStatus', filterStatus);
    sessionStorage.setItem('filterTimestamp', filterTimestamp);
  }, [filterId, filterVorname, filterNachname, filterStatus, filterTimestamp, filtersLoaded]);

  useEffect(() => {
    const fetchAntraege = async () => {

      if (!user || !user.id) {
        console.error('No user found in the auth context');
        return;
      }
      setIsLoading(true);

      try {
        const response = await axios.get<Antrag[]>(`${API_BASE_URL}/application/user/antraege`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        });
        setAntraege(response.data)
        setFilteredAntraege(response.data)
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAntraege();
  }, [user]);


  const applyFilters = () => {
    let tempAntraege = antraege;

    if (filterId) {
      tempAntraege = tempAntraege.filter(antrag => antrag.id.toString().includes(filterId));
    }

    if (filterVorname) {
      tempAntraege = tempAntraege.filter(antrag => antrag.vorname.toLowerCase().includes(filterVorname.toLowerCase()));
    }

    if (filterNachname) {
      tempAntraege = tempAntraege.filter(antrag => antrag.nachname.toLowerCase().includes(filterNachname.toLowerCase()));
    }

    if (filterStatus) {
      tempAntraege = tempAntraege.filter(antrag => antrag.status === filterStatus);
    }

    if (filterTimestamp) {
      tempAntraege = tempAntraege.filter(antrag =>
        new Date(antrag.antragsdatum).toLocaleDateString('de-DE').includes(filterTimestamp)
      );
    }

    setFilteredAntraege(tempAntraege);
  };


  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId, filterVorname, filterNachname, filterStatus, filterTimestamp, antraege]);

  const scrollToPosition = (value: number) => {
    const isMobile = window.innerWidth <= 768;
    const topValue = isMobile ? value : 100;

    window.scrollTo({ top: topValue, behavior: 'smooth' });
  };

  const handleAddAntrag = () => {
    navigate('/antrag', { state: { createAntrag: true } });
    scrollToPosition(460);
  };

  const handleView = (antrag: Antrag) => {
    navigate(`/antrag`, { state: { id: antrag.id, viewAntrag: true, status: antrag.status } });
    scrollToPosition(520);
    sessionStorage.setItem('antragId', antrag.id.toString());
  };

  const handleEdit = (antrag: Antrag) => {
    navigate(`/antrag`, { state: { id: antrag.id, editAntrag: true } });
    scrollToPosition(460);
    sessionStorage.setItem('antragId', antrag.id.toString());
  };

  const handleDelete = (id: number) => {
    setAntragToDelete(id);
    setIsConfirmationOpen(true);
  };

  const handleOpenKorrespondenz = (antrag: Antrag) => {
    navigate(`/dashboard/korrespondenz`, { state: { antragId: antrag.id } });
    scrollToPosition(0);
    sessionStorage.setItem('antragId', antrag.id.toString());
  };

  const confirmDelete = async () => {
    if (antragToDelete !== null) {
      toast.promise(
        axios.delete(`${API_BASE_URL}/application/user/${user?.id}/antrag/${antragToDelete}`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`
          }
        }),
        {
          pending: 'Antrag wird gelöscht...',
          success: 'Antrag erfolgreich gelöscht',
          error: 'Fehler beim Löschen des Antrags'
        }
      ).then(() => {
        setAntraege(prevAntraege => prevAntraege.filter(antrag => antrag.id !== antragToDelete));
        setFilteredAntraege(prevFilteredAntraege => prevFilteredAntraege.filter(antrag => antrag.id !== antragToDelete));
      }).catch((error) => {
        console.error('Error deleting application:', error);
      }).finally(() => {
        setIsConfirmationOpen(false);
        setAntragToDelete(null);
      });
    }
  };


  const handleDownload = async (antrag: Antrag) => {
    setDownloadingId(antrag.id);
    try {
      const response = await axios.get(`${API_BASE_URL}/application/antrag/${antrag.id}/billing`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      var billingData = response.data;
    } catch (error) {
      console.error('Error fetching billing data:', error);
    }

    try {
      const existingPdfBytes = await fetch('VorlageAR_Nostri-Taxen.pdf').then((res) =>
        res.arrayBuffer()
      );

      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const form = pdfDoc.getForm();

      const nameField = form.getTextField('Name');
      nameField.setText(`${antrag.vorname} ${antrag.nachname}`);

      const dateField = form.getTextField('Datum');
      dateField.setText(new Date(billingData.datum).toLocaleDateString('de-DE'));

      const numberField = form.getTextField('Rechnungsnummer');
      numberField.setText(billingData.rechnungsnummer);

      const stripeDateField = form.getTextField('StripeDatum');
      stripeDateField.setText(new Date(billingData.stripeDatum).toLocaleDateString('de-DE'));

      const companyNameField = form.getTextField('Firmenname');
      companyNameField.setText(billingData.firmenname);

      const companyStreetField = form.getTextField('Adresse');
      companyStreetField.setText(billingData.strasse);

      const cityField = form.getTextField('Ort');
      cityField.setText(billingData.plz + ' ' + billingData.ort);

      const uidField = form.getTextField('UID');
      uidField.setText(billingData.uid);

      form.flatten();
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], {
        type: 'application/pdf',
      });
      saveAs(blob, `Rechnung_${antrag.vorname}_${antrag.nachname}.pdf`);
    } catch (error) {
      console.error('Error processing PDF:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleAgencyDownload = async (antrag: Antrag) => {
    setDownloadingId(antrag.id);
    try {
      const response = await axios.get(`${API_BASE_URL}/application/antrag/${antrag.id}/agency-billing`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      var billingData = response.data;
    } catch (error) {
      console.error('Error fetching billing data:', error);
    }

    try {
      const existingPdfBytes = await fetch('Vorlage_Agentur_Stripe.pdf').then((res) =>
        res.arrayBuffer()
      );

      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const form = pdfDoc.getForm();

      const nameField = form.getTextField('Nostrifikant');
      nameField.setText(billingData.nostrifikant);

      const dateField = form.getTextField('Datum');
      dateField.setText(new Date(billingData.datum).toLocaleDateString('de-DE'));

      const numberField = form.getTextField('Rechnungsnummer');
      numberField.setText(billingData.rechnungsnummer);

      const stripeDateField = form.getTextField('StripeDatum');
      stripeDateField.setText(new Date(billingData.stripeDatum).toLocaleDateString('de-DE'));

      const companyNameField = form.getTextField('Name');
      companyNameField.setText(billingData.name);

      const companyStreetField = form.getTextField('Adresse');
      companyStreetField.setText(billingData.strasse);

      const cityField = form.getTextField('Ort');
      cityField.setText(billingData.plz + ' ' + billingData.ort);

      const uidField = form.getTextField('UID');
      uidField.setText(billingData.uid);

      form.flatten();
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], {
        type: 'application/pdf',
      });
      saveAs(blob, `Rechnung_${antrag.vorname}_${antrag.nachname}.pdf`);
    } catch (error) {
      console.error('Error processing PDF:', error);
    } finally {
      setDownloadingId(null);
    }
  };


  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'Noch nicht eingereicht':
        return 'red';
      case 'Eingereicht':
        return '#f7dd14';
      case 'Abgeschlossen':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'Noch nicht eingereicht':
        return 'Noch nicht eingereicht';
      case 'Eingereicht':
        return 'Eingereicht';
      case 'Abgeschlossen':
        return 'Abgeschlossen';
      default:
        return 'Unbekannt';
    }
  };

  const handleSort = (column: keyof Antrag) => {
    let direction = 'asc' as 'asc' | 'desc' | null;
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        direction = 'desc';
      } else if (sortDirection === 'desc') {
        direction = null;
        setSortColumn(null);
        setSortDirection('asc');
        applyFilters(); // Re-apply filters to get the filteredAntraege
        return;
      }
    } else {
      setSortColumn(column);
    }

    setSortDirection(direction || 'asc');

    const sortedAntraege = [...filteredAntraege].sort((a, b) => {
      if (!direction) {
        return 0;
      }

      let aValue: string | number = a[column] as string | number;
      let bValue: string | number = b[column] as string | number;

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }

      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      } else if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      } else {
        return 0;
      }
    });

    setFilteredAntraege(direction ? sortedAntraege : [...filteredAntraege]);
  };


  const renderSortIcon = (column: keyof Antrag) => {
    if (sortColumn !== column) {
      return null;
    }

    return sortDirection === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>;
  };

  const getButtonStyle = (isDisabled: boolean, baseStyle: React.CSSProperties): React.CSSProperties => ({
    ...baseStyle,
    backgroundColor: isDisabled ? '#dcdcdc' : baseStyle.backgroundColor,
    color: isDisabled ? '#a9a9a9' : baseStyle.color,
    textDecoration: isDisabled ? 'line-through' : 'none',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    borderColor: isDisabled ? '#dcdcdc' : baseStyle.borderColor,
  });

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes blinker {
        50% { opacity: 0.3; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: 'auto', position: 'relative' }}>
      <h2 className='antragheading'>
        Meine Anträge
      </h2>
      {isLoading ? (
        <div className='loader'></div>
      ) : (
        <div>
          <div className="filter-container">
            <input
              name='filterId'
              type="text"
              placeholder="Filtern nach ID"
              value={filterId}
              onChange={(e) => setFilterId(e.target.value)}
              className="filter-input"
            />
            <input
              name='filterVorname'
              type="text"
              placeholder="Filtern nach Vorname"
              value={filterVorname}
              onChange={(e) => setFilterVorname(e.target.value)}
              className="filter-input"
            />
            <input
              name='filterNachname'
              type="text"
              placeholder="Filtern nach Nachname"
              value={filterNachname}
              onChange={(e) => setFilterNachname(e.target.value)}
              className="filter-input"
            />
            <select
              name='filterStatus'
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="">Filtern nach Status</option>
              <option value="Noch nicht eingereicht">Noch nicht eingereicht</option>
              <option value="Eingereicht">Eingereicht</option>
              <option value="Abgeschlossen">Abgeschlossen</option>
            </select>
            <input
              name='filterTimestamp'
              type="text"
              placeholder="Filtern nach Antragsdatum"
              value={filterTimestamp}
              onChange={(e) => setFilterTimestamp(e.target.value)}
              className="filter-input"
            />
          </div>
          {filterId || filterVorname || filterNachname || filterStatus || filterTimestamp ? (
            <div style={{ marginBottom: '20px', }}>
              <span tabIndex={0} className="reset-filters" role="button"
                onClick={() => {
                  setFilterId('');
                  setFilterVorname('');
                  setFilterNachname('');
                  setFilterStatus('');
                  setFilterTimestamp('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setFilterId('');
                    setFilterVorname('');
                    setFilterNachname('');
                    setFilterStatus('');
                    setFilterTimestamp('');
                  }
                }}
              >
                <RiResetLeftFill style={{ marginRight: '5px' }} /> Filter zurücksetzen
              </span>
            </div>
          ) : null}
          <div className={`table-container`}>
            <table className='sticky-table'>
              <thead>
                <tr>
                  <th className='table-header' onClick={() => handleSort('id')}>
                    ID {renderSortIcon('id')}
                  </th>
                  <th className='table-header' onClick={() => handleSort('vorname')}>
                    Vorname {renderSortIcon('vorname')}
                  </th>
                  <th className='table-header' onClick={() => handleSort('nachname')}>
                    Nachname {renderSortIcon('nachname')}
                  </th>
                  <th className='table-header' onClick={() => handleSort('land')}>
                    Nationalität {renderSortIcon('land')}
                  </th>
                  <th className='table-header' onClick={() => handleSort('ausbildungsland')}>
                    Ausbildungsland {renderSortIcon('ausbildungsland')}
                  </th>
                  <th className='table-header' onClick={() => handleSort('status')}>
                    Status {renderSortIcon('status')}
                  </th>
                  <th className='table-header' onClick={() => handleSort('antragsdatum')}>
                    Antragsdatum {renderSortIcon('antragsdatum')}
                  </th>
                  <th className='table-header2'>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredAntraege.map(antrag => (
                  <tr
                    key={antrag.id}
                    className={`table-row`}
                  /*onClick={(e) => {
                    if (!(e.target as HTMLElement).classList.contains('action-button') &&
                      !(e.target as HTMLElement).closest('.action-button')) {
                      handleView(antrag);
                    }
                  }}*/
                  >
                    <td style={tableCellStyle}>{antrag.id}</td>
                    <td style={tableCellStyle}>{antrag.vorname}</td>
                    <td style={tableCellStyle}>{antrag.nachname}</td>
                    <td style={tableCellStyle}>{antrag.land}</td>
                    <td style={tableCellStyle}>{antrag.ausbildungsland}</td>
                    <td style={tableCellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(antrag.status),
                            marginRight: '8px',
                            filter: 'drop-shadow(5px 5px 5px rgba(0, 0, 0, 0.3))'
                          }}
                        ></span>
                        {getStatusLabel(antrag.status)}
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      {new Date(antrag.antragsdatum).toLocaleDateString('de-DE')}
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                        <button
                          type='button'
                          className='action-button'
                          title="Antrag anzeigen"
                          style={detailButtonStyle}
                          onClick={() => handleView(antrag)}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          type='button'
                          className='action-button'
                          title="Antrag bearbeiten"
                          disabled={antrag.status !== 'Noch nicht eingereicht'}
                          style={getButtonStyle(antrag.status === 'Eingereicht' || antrag.status === 'Abgeschlossen', editButtonStyle)}
                          onClick={() => handleEdit(antrag)}

                        >
                          <i className="bi bi-pencil-fill"></i>
                        </button>
                        <button
                          type='button'
                          className='action-button'
                          title="Antrag löschen"
                          disabled={antrag.status !== 'Noch nicht eingereicht'}
                          style={getButtonStyle(antrag.status === 'Eingereicht' || antrag.status === 'Abgeschlossen', deleteButtonStyle)}
                          onClick={() => handleDelete(antrag.id)}
                        >
                          <i className="bi bi-trash-fill"></i>
                        </button>
                        {antrag.hatRechnung && (
                          <button
                            type='button'
                            className='action-button'
                            title="Rechnung herunterladen"
                            disabled={antrag.status === 'Noch nicht eingereicht'}
                            style={getButtonStyle(antrag.status === 'Noch nicht eingereicht', billButtonStyle)}
                            onClick={() => {
                              if (user?.role === 'NostrifyUser') {
                                handleDownload(antrag);
                              } else {
                                handleAgencyDownload(antrag);
                              }
                            }}
                          >
                            {downloadingId === antrag.id ? (
                              <div className="loader-small"></div>
                            ) : (
                              <i className="bi bi-file-earmark-pdf"></i>
                            )}
                          </button>
                        )}
                        {antrag.status !== 'Noch nicht eingereicht' && (
                          <button
                            type='button'
                            className='action-button'
                            title="Korrespondenz"
                            style={
                              antrag.neueNachricht
                                ? { ...chatButtonStyle, ...blinkingStyle }
                                : chatButtonStyle
                            }
                            onClick={() => handleOpenKorrespondenz(antrag)}
                          >
                            <i className="bi bi-envelope"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <button type='button' onClick={handleAddAntrag} className="antragbutton">
        Neuen Antrag hinzufügen
      </button>

      {/*       {user?.email === 'sebastian.miloczki@imc.ac.at' || user?.email === 'elisabeth.huber@imc.ac.at' || user?.email === 'heide.hoda@imc.ac.at' ? (
        <button type='button' onClick={handleAddAntrag} className="antragbutton">
          Neuen Antrag hinzufügen
        </button>
      ) : (
        <p style={{ color: 'white', marginTop: '20px' }}>
          Die IMC-Nostrifizierungs-Plattform befindet sich derzeit noch in der Entwickling.<br />
          Bitte informieren Sie sich über die Nostrifizierung am IMC Krems unter folgendem Link :<br />
          <a href="https://www.imc.ac.at/services/noe-kompetenzstelle-fuer-nostrifizierungen/" target="_blank" rel="noopener noreferrer">IMC Krems Nostrifizierung</a>
        </p>
      )} */}

      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onRequestClose={() => setIsConfirmationOpen(false)}
        onConfirm={confirmDelete}
        message="Möchten Sie diesen Antrag wirklich löschen?"
        title="Antrag löschen"
        confirmButtonText="Ja"
        cancelButtonText="Nein"
      />
    </div>
  );
};

const tableCellStyle: React.CSSProperties = {
  padding: '12px',
  borderBottom: '1px solid #ddd',
  textAlign: 'left',
  fontSize: '14px',
};

const editButtonStyle: React.CSSProperties = {
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  padding: '5px 12px',
  cursor: 'pointer',
  fontSize: '14px',
  transition: 'background-color 0.3s ease',
};

const detailButtonStyle: React.CSSProperties = {
  backgroundColor: 'blue',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  padding: '5px 12px',
  cursor: 'pointer',
  fontSize: '14px',
  transition: 'background-color 0.3s ease',
};

const deleteButtonStyle: React.CSSProperties = {
  backgroundColor: '#dc3545',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  padding: '5px 12px',
  cursor: 'pointer',
  fontSize: '14px',
  transition: 'background-color 0.3s ease',
};

const billButtonStyle: React.CSSProperties = {
  backgroundColor: '#ffc107',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  padding: '5px 12px',
  cursor: 'pointer',
  fontSize: '14px',
  transition: 'background-color 0.3s ease',
};

const chatButtonStyle: React.CSSProperties = {
  backgroundColor: '#505050ff',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  padding: '5px 12px',
  cursor: 'pointer',
  fontSize: '14px',
  transition: 'background-color 0.3s ease',
};

const blinkingStyle: React.CSSProperties = {
  animation: 'blinker 1s linear infinite',
};


export default UserDashboard;
